import { QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, Routes } from 'react-router-dom';

import { LoadingScreen } from '@/components/common/LoadingScreen';
import { SignInScreen } from '@/components/common/SignInScreen';
import { AppLayout } from '@/components/layout/AppLayout';
import { Toaster } from '@/components/ui/toast';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { ThemeProvider } from '@/hooks/useTheme';
import { queryClient } from '@/lib/queryClient';
import { BoardListPage } from '@/pages/board/BoardListPage';
import { BoardPostDetailPage } from '@/pages/board/BoardPostDetailPage';
import { BoardPostEditorPage } from '@/pages/board/BoardPostEditorPage';
import { CampaignBoardPage } from '@/pages/campaigns/CampaignBoardPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { FormBuilderPage } from '@/pages/forms/FormBuilderPage';
import { FormSettingsPage } from '@/pages/forms/FormSettingsPage';
import { FormCompletionNotificationPage } from '@/pages/forms/FormCompletionNotificationPage';
import { FormSubmissionsPage } from '@/pages/forms/FormSubmissionsPage';
import { LandingEditorPage } from '@/pages/landings/LandingEditorPage';
import { PublicLandingPage } from '@/pages/landings/public/PublicLandingPage';
import { AdImagesListPage } from '@/pages/ad-images/AdImagesListPage';
import { AdImageEditorPage } from '@/pages/ad-images/AdImageEditorPage';
import { LiffFormPage } from '@/pages/public/forms/LiffFormPage';
import { PublicFormPage } from '@/pages/public/forms/PublicFormPage';
import { DeliveriesPage } from '@/pages/line/deliveries/DeliveriesPage';
import { EventsPage } from '@/pages/line/events/EventsPage';
import { LineNewsPage } from '@/pages/line/news/LineNewsPage';
import { OfferingsPage } from '@/pages/line/offerings/OfferingsPage';
import { QaReviewPage } from '@/pages/line/qa-review/QaReviewPage';
import { UsersPage } from '@/pages/line/users/UsersPage';
import { MailAnalysisPage } from '@/pages/mail/analysis/MailAnalysisPage';
import { MailContactsPage } from '@/pages/mail/contacts/MailContactsPage';
import { MailGeneratePage } from '@/pages/mail/generate/MailGeneratePage';
import { MailListPage } from '@/pages/mail/list/MailListPage';
import { MailSchedulesPage } from '@/pages/mail/schedules/MailSchedulesPage';
import { MailSettingsPage } from '@/pages/mail/settings/MailSettingsPage';
import { PromptPresetsPage } from '@/pages/prompts/presets/PromptPresetsPage';
import { PromptTemplatesPage } from '@/pages/prompts/templates/PromptTemplatesPage';
import { BusinessMailTemplatesPage } from '@/pages/business-mail-templates/BusinessMailTemplatesPage';
import { DealDetailPage } from '@/pages/deals/DealDetailPage';
import { DealsInboxPage } from '@/pages/deals/inbox/DealsInboxPage';
import { MergeCandidateDetailPage } from '@/pages/deals/inbox/MergeCandidateDetailPage';
import { BriefingsPage } from '@/pages/briefings/BriefingsPage';
import { BriefingRecordPage } from '@/pages/briefings/BriefingRecordPage';
import { SalesContactsPage } from '@/pages/sales-contacts/SalesContactsPage';
import { SalesContactDetailPage } from '@/pages/sales-contacts/SalesContactDetailPage';
import { UsersPage as AdminUsersPage } from '@/pages/admin/UsersPage';
import { UserDetailPage as AdminUserDetailPage } from '@/pages/admin/UserDetailPage';
import { SaManagementPage } from '@/pages/sa-management/SaManagementPage';
import { InvitePage } from '@/pages/public/invite/InvitePage';
import { ReferralPage } from '@/pages/referral/ReferralPage';
import { QuotesListPage } from '@/pages/quotes/QuotesListPage';
import { QuoteDetailPage } from '@/pages/quotes/QuoteDetailPage';
import { InvoicesListPage } from '@/pages/invoices/InvoicesListPage';
import { PurchaseOrdersListPage } from '@/pages/purchase-orders/PurchaseOrdersListPage';
import { DeliveriesListPage } from '@/pages/deliveries/DeliveriesListPage';
import { SoldDevicesListPage } from '@/pages/sold-devices/SoldDevicesListPage';
import { CommissionSetCategoriesPage } from '@/pages/commissions/CommissionSetCategoriesPage';
import { CommissionDetailPage } from '@/pages/commissions/CommissionDetailPage';
import { CommissionInvoicePublicPage } from '@/pages/commission-invoices/CommissionInvoicePublicPage';
import { SaDashboardPage } from '@/pages/sa-dashboard/SaDashboardPage';
import { SaDealsPage } from '@/pages/sa-dashboard/SaDealsPage';
import { SaFormsPage } from '@/pages/sa-dashboard/SaFormsPage';
import { SaCatalogsPage } from '@/pages/sa-dashboard/SaCatalogsPage';
import { SaDocumentsPage } from '@/pages/sa-dashboard/SaDocumentsPage';
import { SaCommissionCalcPage } from '@/pages/sa-dashboard/SaCommissionCalcPage';
import { SaProfilePage } from '@/pages/sa-dashboard/SaProfilePage';
import { SaContactDetailPage } from '@/pages/sa-dashboard/SaContactDetailPage';
import { SaContactsPage } from '@/pages/sa-dashboard/SaContactsPage';
import { DocumentsListPage } from '@/pages/documents/DocumentsListPage';
import { DocumentEditorPage } from '@/pages/documents/DocumentEditorPage';
import { PdfDocumentViewerPage } from '@/pages/documents/PdfDocumentViewerPage';
import { ConsentDocumentsListPage } from '@/pages/consent-documents/ConsentDocumentsListPage';
import { ConsentDocumentEditorPage } from '@/pages/consent-documents/ConsentDocumentEditorPage';
import { PublicQuotePage } from '@/pages/public/quote/PublicQuotePage';
import { PublicInvoicePage } from '@/pages/public/invoice/PublicInvoicePage';
import { PublicFormAnalyticsPage } from '@/pages/public/form-analytics/PublicFormAnalyticsPage';
import { PublicDocumentPage } from '@/pages/public/document/PublicDocumentPage';
import { PublicConsentPage } from '@/pages/public/consent/PublicConsentPage';
import { paths } from '@/routes/paths';

// v3.3 IA 刷新 — Hub ページ
import { CampaignsHubPage } from '@/pages/campaigns/CampaignsHubPage';
import { MailHubPage } from '@/pages/mail/MailHubPage';
import { LineHubPage } from '@/pages/line/LineHubPage';
import { AudienceHubPage } from '@/pages/audience/AudienceHubPage';
import { PromptsHubPage } from '@/pages/prompts/PromptsHubPage';
import { InquiryHubPage } from '@/pages/inquiry/InquiryHubPage';
import { DealsHubPage } from '@/pages/deals/DealsHubPage';
import { CustomersHubPage } from '@/pages/customers/CustomersHubPage';
import { OrdersHubPage } from '@/pages/orders/OrdersHubPage';
import { FinanceHubPage } from '@/pages/finance/FinanceHubPage';
import { MasterProductsHubPage } from '@/pages/master-products/MasterProductsHubPage';
import { SalesConfigHubPage } from '@/pages/sales-settings/SalesConfigHubPage';
import { SaHubPage } from '@/pages/sa-dashboard/SaHubPage';

// v3.3.2: システム Hub 解体 — ユーザーと全体設定を直接ルーティング
import { SettingsPage } from '@/pages/settings/SettingsPage';

function ProtectedRoutes() {
  const { status } = useAuth();
  if (status === 'loading') return <LoadingScreen />;
  if (status !== 'signed-in') return <SignInScreen />;
  return <AppLayout />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* 公開ルート（未認証アクセス可、AppLayout 外） */}
      <Route path={paths.publicForm} element={<PublicFormPage />} />
      <Route path={paths.liffForm} element={<LiffFormPage />} />
      <Route path={paths.publicLanding} element={<PublicLandingPage />} />
      <Route path={paths.publicQuote} element={<PublicQuotePage />} />
      <Route path={paths.publicInvoice} element={<PublicInvoicePage />} />
      <Route path={paths.publicFormAnalytics} element={<PublicFormAnalyticsPage />} />
      <Route path={paths.publicDocument} element={<PublicDocumentPage />} />
      <Route path={paths.publicConsent} element={<PublicConsentPage />} />
      <Route path={paths.publicInvite} element={<InvitePage />} />
      {/* SA 請求書 公開ページ (v3.2.14 / Phase 3a) */}
      <Route path={paths.publicSaInvoice} element={<CommissionInvoicePublicPage />} />

      <Route element={<ProtectedRoutes />}>
        <Route path={paths.dashboard} element={<DashboardPage />} />

        {/* 掲示板 (v3.5 Board / Phase 1) */}
        <Route path={paths.board} element={<BoardListPage />} />
        <Route path={paths.boardNew} element={<BoardPostEditorPage />} />
        <Route path={paths.boardPostDetail} element={<BoardPostDetailPage />} />
        <Route path={paths.boardPostEdit} element={<BoardPostEditorPage />} />

        {/* ─────────────────────────────────────────────
            v3.3 IA 刷新: 親メニュー (Hub) ルート
            既存の直接ルートも互換のため残置。
           ───────────────────────────────────────────── */}
        {/* /campaigns は Hub に置き換え (既存 CampaignsListPage は Hub の "ファネル一覧" タブで描画) */}
        <Route path={paths.campaignsHub} element={<CampaignsHubPage />} />
        <Route path={paths.mailHub} element={<MailHubPage />} />
        <Route path={paths.lineHub} element={<LineHubPage />} />
        <Route path={paths.audienceHub} element={<AudienceHubPage />} />
        <Route path={paths.promptsHub} element={<PromptsHubPage />} />
        <Route path={paths.inquiryHub} element={<InquiryHubPage />} />
        {/* /deals は Hub に置き換え (既存 DealsBoardPage は Hub の "商談ボード" タブで描画) */}
        <Route path={paths.dealsHub} element={<DealsHubPage />} />
        <Route path={paths.customersHub} element={<CustomersHubPage />} />
        <Route path={paths.ordersHub} element={<OrdersHubPage />} />
        <Route path={paths.financeHub} element={<FinanceHubPage />} />
        <Route path={paths.saHub} element={<SaHubPage />} />
        <Route path={paths.productsHub} element={<MasterProductsHubPage />} />
        <Route path={paths.salesConfigHub} element={<SalesConfigHubPage />} />
        {/* v3.3.2: システム Hub 解体 — /admin/users と /settings を直接マウント */}
        <Route path={paths.settings} element={<SettingsPage />} />

        {/* ─── 旧パス互換リダイレクト (v3.3 IA) ─── */}
        {/* Hub の内部タブは `?hub=` を使う (内部ページの `?tab=` と分離) */}
        <Route path={paths.leads} element={<Navigate to={`${paths.audienceHub}?hub=leads`} replace />} />
        <Route path={paths.products} element={<Navigate to={`${paths.productsHub}?hub=catalogs`} replace />} />
        <Route path={paths.productBundles} element={<Navigate to={`${paths.productsHub}?hub=bundles`} replace />} />
        <Route path={paths.commissions} element={<Navigate to={`${paths.productsHub}?hub=commissions`} replace />} />
        <Route path={paths.salesSettings} element={<Navigate to={paths.salesConfigHub} replace />} />
        <Route path={paths.dealsTrash} element={<Navigate to={`${paths.dealsHub}?hub=trash`} replace />} />
        <Route path={paths.staleDeals} element={<Navigate to={`${paths.dealsHub}?hub=stale`} replace />} />
        {/* v3.3.2: システム Hub 廃止、システムログは Settings > 監査ログ タブへ */}
        <Route path={paths.systemLogs} element={<Navigate to={paths.settings} replace />} />
        <Route path={paths.adminHub} element={<Navigate to={paths.settings} replace />} />
        <Route path={paths.commissionLedger} element={<Navigate to={`${paths.financeHub}?hub=ledger`} replace />} />
        <Route path={paths.saInvoices} element={<Navigate to={`${paths.financeHub}?hub=invoices`} replace />} />

        {/* キャンペーン (旧パス) */}
        <Route path={paths.campaignBoard} element={<CampaignBoardPage />} />

        {/* ランディング */}
        <Route
          path={paths.landings}
          element={<Navigate to={`${paths.campaignsHub}?hub=landings`} replace />}
        />
        <Route path={paths.landingEdit} element={<LandingEditorPage />} />

        {/* 広告画像生成 */}
        <Route path={paths.adImages} element={<AdImagesListPage />} />
        <Route path={paths.adImageEdit} element={<AdImageEditorPage />} />

        {/* フォーム */}
        <Route
          path={paths.forms}
          element={<Navigate to={`${paths.campaignsHub}?hub=forms`} replace />}
        />
        <Route path={paths.formBuilder} element={<FormBuilderPage />} />
        <Route path={paths.formSettings} element={<FormSettingsPage />} />
        <Route
          path={paths.formCompletion}
          element={<FormCompletionNotificationPage />}
        />
        <Route
          path={paths.formSubmissions}
          element={<FormSubmissionsPage />}
        />

        {/* リフェラル（タグ付き URL 発行 + チャネル管理） */}
        <Route path={paths.referral} element={<ReferralPage />} />
        {/* 旧パス互換：リダイレクト */}
        <Route
          path={paths.urlGenerator}
          element={<Navigate to={paths.referral} replace />}
        />
        <Route
          path={paths.referralChannels}
          element={<Navigate to={`${paths.referral}?tab=channels`} replace />}
        />

        {/* LINE Bot */}
        <Route path={paths.lineNews} element={<LineNewsPage />} />
        <Route path={paths.lineUsers} element={<UsersPage />} />
        {/* 旧パス互換：/line/sessions → /line/users?tab=sessions */}
        <Route
          path={paths.lineSessions}
          element={<Navigate to={`${paths.lineUsers}?tab=sessions`} replace />}
        />
        <Route path={paths.lineEvents} element={<EventsPage />} />
        <Route path={paths.lineDeliveries} element={<DeliveriesPage />} />
        <Route path={paths.lineOfferings} element={<OfferingsPage />} />
        <Route path={paths.lineQaReview} element={<QaReviewPage />} />

        {/* 統一プロンプト管理（Mail / LINE NEWS 共通） */}
        <Route path={paths.promptTemplates} element={<PromptTemplatesPage />} />
        <Route path={paths.promptPresets} element={<PromptPresetsPage />} />

        {/* Mail */}
        <Route path={paths.mailGenerate} element={<MailGeneratePage />} />
        <Route path={paths.mailList} element={<MailListPage />} />
        <Route path={paths.mailContacts} element={<MailContactsPage />} />
        <Route path={paths.mailSchedules} element={<MailSchedulesPage />} />
        <Route path={paths.mailAnalysis} element={<MailAnalysisPage />} />
        <Route path={paths.mailSettings} element={<MailSettingsPage />} />

        {/* 業務メールテンプレ (営業設定 Hub から内部リンクされる) */}
        <Route path={paths.businessMailTemplates} element={<BusinessMailTemplatesPage />} />

        {/* 商談 個別ページ — 商談 Hub の "ボード" タブから遷移 */}
        <Route path={paths.dealsInbox} element={<DealsInboxPage />} />
        <Route path={paths.dealsInboxDetail} element={<MergeCandidateDetailPage />} />
        <Route path={paths.dealDetail} element={<DealDetailPage />} />
        <Route path={paths.salesContacts} element={<SalesContactsPage />} />
        <Route path={paths.salesContactDetail} element={<SalesContactDetailPage />} />

        {/* 個別説明 (Phase 5) */}
        <Route path={paths.briefings} element={<BriefingsPage />} />
        <Route path={paths.briefingRecord} element={<BriefingRecordPage />} />

        {/* 受発注 個別ページ — 受発注 Hub から遷移 */}
        <Route path={paths.quotes} element={<QuotesListPage />} />
        <Route path={paths.quoteDetail} element={<QuoteDetailPage />} />
        <Route path={paths.invoices} element={<InvoicesListPage />} />
        <Route path={paths.purchaseOrders} element={<PurchaseOrdersListPage />} />
        <Route path={paths.deliveries} element={<DeliveriesListPage />} />
        <Route path={paths.soldDevices} element={<SoldDevicesListPage />} />

        {/* 報酬設定 詳細遷移 (Hub の "報酬設定" タブから) */}
        <Route
          path={paths.commissionSetCategories}
          element={<CommissionSetCategoriesPage />}
        />
        <Route
          path={paths.commissionCategoryEditor}
          element={<CommissionDetailPage />}
        />

        {/* セールスエージェント 個別ページ — SA ポータル Hub からカード遷移 */}
        <Route path={paths.saDashboard} element={<SaDashboardPage />} />
        <Route path={paths.saDeals} element={<SaDealsPage />} />
        <Route path={paths.saForms} element={<SaFormsPage />} />
        <Route path={paths.saCatalogs} element={<SaCatalogsPage />} />
        <Route path={paths.saDocuments} element={<SaDocumentsPage />} />
        <Route path={paths.saCommissionCalc} element={<SaCommissionCalcPage />} />
        <Route path={paths.saProfile} element={<SaProfilePage />} />
        <Route path={paths.saContacts} element={<SaContactsPage />} />
        <Route
          path={paths.saContactDetail}
          element={<SaContactDetailPage />}
        />

        {/* 資料管理 (Phase 8) */}
        <Route path={paths.documents} element={<DocumentsListPage />} />
        <Route path={paths.documentEdit} element={<DocumentEditorPage />} />
        {/* v3.2.28+: PDF ダウンロード型資料ビューワー */}
        <Route
          path={paths.pdfDocumentViewer}
          element={<PdfDocumentViewerPage />}
        />
        <Route
          path={paths.consentDocuments}
          element={<ConsentDocumentsListPage />}
        />
        <Route
          path={paths.consentDocumentEdit}
          element={<ConsentDocumentEditorPage />}
        />

        {/* ユーザー管理 詳細 (システム Hub の "ユーザー" タブから遷移) */}
        <Route path={paths.adminUsers} element={<AdminUsersPage />} />
        <Route path={paths.adminUserDetail} element={<AdminUserDetailPage />} />

        {/* 外部SA 管理 (v3.2.30) */}
        <Route path={paths.saManagement} element={<SaManagementPage />} />

        <Route path="*" element={<Navigate to={paths.dashboard} replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <AppRoutes />
          <Toaster />
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
